import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2RefreshedBanner.module.scss";
import confettiDesktop from "@/assets/images/grid-v2-banner-confetti-desktop.svg";
import confettiMobile from "@/assets/images/grid-v2-banner-confetti-mobile.svg";
import { GridV2RefreshedPill } from "@/components/shared/GridV2RefreshedPill";
import { TenantIconNameEnum } from "@/types/tenantIcons";

export interface GridV2RefreshedBannerProps {
  /** The shopper wants to see what changed: opens the pricing comparison. */
  onLearnWhatsNew: () => void;
  /**
   * The shopper asks to go back to the original fixed pricing. The caller
   * opens the opt-out confirmation (GridV2OptOut); the opt-out happens there.
   */
  onUseOriginalPricing: () => void;
  className?: string;
}

/**
 * Grid V2 notice above the flight results: a yellow-framed box with confetti,
 * the Refreshed pill, one line of copy and the "Learn what's new" link, then
 * the opt-out entry point below the box. Figma 41795:22016 (desktop row) and
 * 41811:431557 (mobile, stacked).
 */
export const GridV2RefreshedBanner = ({
  onLearnWhatsNew,
  onUseOriginalPricing,
  className,
}: GridV2RefreshedBannerProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2RefreshedBanner");

  return (
    <div className={clsx(block, className)}>
      <div className={cn("box")}>
        <div className={cn("content")}>
          <GridV2RefreshedPill />
          <p className={cn("message")}>{t("gridV2.refreshedBanner.message")}</p>
          <button
            type="button"
            className={cn("link")}
            onClick={onLearnWhatsNew}
          >
            {t("gridV2.refreshedBanner.learnWhatsNew")}
            <span className={cn("arrow")} aria-hidden="true">
              <Icon iconName={TenantIconNameEnum.arrowRightAlt} />
            </span>
          </button>
        </div>
        {/* Decorative: one asset per layout, the breakpoint picks which shows. */}
        <img
          className={cn("confetti", ["desktop"])}
          src={confettiDesktop}
          alt=""
        />
        <img
          className={cn("confetti", ["mobile"])}
          src={confettiMobile}
          alt=""
        />
      </div>
      <button
        type="button"
        className={cn("link", ["use-original-pricing"])}
        onClick={onUseOriginalPricing}
      >
        {t("gridV2.refreshedBanner.useOriginalPricing")}
        <span className={cn("arrow")} aria-hidden="true">
          <Icon iconName={TenantIconNameEnum.arrowRightAlt} />
        </span>
      </button>
    </div>
  );
};
